/**
 * ConversationsTab — the Conversations tab body (lot I3, UI-SPEC §2.19).
 *
 * Rows (one per ConversationPersona), an opened row becomes the form in place; then
 * « Désactivés » (user pauses, archives without a reason) and « Mis en pause (offre) »
 * (plan_limit). A failed load is an error with « Réessayer », never the empty state.
 * Every save updates the list in place from its response (C16).
 */
import { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Brand, BrandQuery, BrandQueryAxis, ConversationLoad, ConversationPersona, TargetZone, Tenant } from '@traaker/shared';
import { zoneDisplayLabel } from '@traaker/shared';
import { MONDIAL } from '../promptStudioModel';
import type { LocKey } from '../promptStudioModel';
import { vocabKeyLabel } from '../filterModel';
import { languageName } from '../variantModel';
import {
  applyConvFilter,
  convFacetValues,
  convFilterCount,
  convGroups,
  convPlaceKey,
  covConversations,
  personaTitle,
} from './conversationModel';
import type { ConvFacet, ConvFilter } from './conversationModel';
import type { BrandPersonaListItem } from './conversationsApi';
import { ConversationPausedRow, ConversationRow } from './ConversationRow';
import { ConversationFiltersPanel } from './ConversationFiltersPanel';
import { ConversationForm } from './ConversationForm';

/** The tab's data, loaded by PromptStudioDialog (the footer and Couverture read it too). */
export interface ConvData {
  loading: boolean;
  error: boolean;
  /** A load succeeded since the popup opened. */
  loaded: boolean;
  personas: ConversationPersona[];
  brandPersonas: BrandPersonaListItem[];
  load: ConversationLoad | null;
}

export const EMPTY_CONV_DATA: ConvData = { loading: false, error: false, loaded: false, personas: [], brandPersonas: [], load: null };

export interface ConversationsTabProps {
  brand: Brand;
  tenant?: Tenant;
  data: ConvData;
  queries: BrandQuery[];
  known: Map<LocKey, TargetZone>;
  vocabByAxis: Record<BrandQueryAxis, string[]>;
  q: string;
  filter: ConvFilter;
  onFilter: (f: ConvFilter) => void;
  filtersOpen: boolean;
  onCloseFilters: () => void;
  /** « + Nouvelle conversation »: the form replaces the list. */
  creating: boolean;
  onCreating: (creating: boolean) => void;
  /** The conversation whose form opens with the tab (the persona page's « Modifier »). */
  initialOpenId?: string;
  onRetry: () => void;
  /** A save answered: upsert the row, the footer takes the response's `load`. */
  onSaved: (persona: ConversationPersona, load: ConversationLoad) => void;
  onRemoved: (personaId: string) => void;
  /** A network failure or a conflict: reload the list before anything else is tried. */
  onReload: () => void;
  /** « Gérer les personas ↗ »: closes the popup, opens Brand Knowledge on its personas. */
  onManagePersonas: () => void;
}

export function ConversationsTab({
  brand,
  tenant,
  data,
  queries,
  known,
  vocabByAxis,
  q,
  filter,
  onFilter,
  filtersOpen,
  onCloseFilters,
  creating,
  onCreating,
  initialOpenId,
  onRetry,
  onSaved,
  onRemoved,
  onReload,
  onManagePersonas,
}: ConversationsTabProps) {
  const { t, i18n } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(initialOpenId ?? null);
  const isAgency = tenant?.tier === 'agency';

  const names = useMemo(
    () => new Map(data.brandPersonas.map((p) => [p.brandPersonaId, p.name])),
    [data.brandPersonas],
  );
  const queriesById = useMemo(() => new Map(queries.map((r) => [r.queryId, r])), [queries]);
  const covById = useMemo(() => {
    const out = new Map<string, ReturnType<typeof covConversations>[number]>();
    for (const p of data.personas) {
      const [c] = covConversations([{ ...p, status: 'active' }], queriesById, vocabByAxis, known);
      if (c) out.set(p.personaId, c);
    }
    return out;
  }, [data.personas, queriesById, vocabByAxis, known]);

  // Lieu facet labels, keyed like the facet values (`convPlaceKey`).
  const zoneLabels = useMemo(() => {
    const out = new Map<LocKey, string>();
    for (const p of data.personas) {
      for (const c of p.cells ?? []) {
        if (!c.zoneMeta) continue;
        const key = convPlaceKey(c.zoneMeta, known);
        if (!out.has(key)) out.set(key, zoneDisplayLabel(known.get(key) ?? c.zoneMeta));
      }
    }
    return out;
  }, [data.personas, known]);

  const intentLabel = (id: ConversationPersona['intentId']) =>
    t(`conversations.intent${id.charAt(0).toUpperCase()}${id.slice(1)}`);
  const values = (p: ConversationPersona) => convFacetValues(p, covById.get(p.personaId), known);
  const searchText = (p: ConversationPersona) => `${personaTitle(p, names)} ${intentLabel(p.intentId)} ${(p.tags ?? []).join(' ')}`;
  const labelOf = (facet: ConvFacet, v: string): string => {
    switch (facet) {
      case 'persona': return names.get(v) ?? t('promptStudio.conv.noPersona');
      case 'intent': return intentLabel(v as ConversationPersona['intentId']);
      case 'vocab': return vocabKeyLabel(v);
      case 'zone': return v === MONDIAL ? t('conversations.zoneWorld') : zoneLabels.get(v) ?? v;
      case 'language': return languageName(v, i18n.language);
      default: return v;
    }
  };

  if (data.loading && !data.loaded) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ height: 46, mx: 2, my: 0.5, bgcolor: 'rgba(0,0,0,.04)', borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>{t('promptStudio.conv.loadFailed')}</Box>
        <Button size="small" onClick={onRetry}>{t('promptStudio.conv.retry')}</Button>
      </Box>
    );
  }

  const allTags = [...new Set(data.personas.flatMap((p) => p.tags ?? []))].sort();
  const formFor = (original: ConversationPersona | null, close: () => void) => (
    <ConversationForm
      key={original?.personaId ?? 'new'}
      brand={brand}
      tenant={tenant}
      original={original}
      brandPersonas={data.brandPersonas}
      queries={queries}
      known={known}
      load={data.load}
      allTags={allTags}
      onCancel={close}
      onSaved={(persona, load) => { close(); onSaved(persona, load); }}
      onRemoved={(id) => { close(); onRemoved(id); }}
      onReload={onReload}
      onManagePersonas={onManagePersonas}
    />
  );

  // A new conversation: the same form, replacing the list (no column header, UI-SPEC §2.19).
  if (creating) {
    return <Box sx={{ flex: 1, overflowY: 'auto' }}>{formFor(null, () => onCreating(false))}</Box>;
  }

  const groups = convGroups(applyConvFilter(data.personas, q, filter, values, searchText));
  const hasFilter = convFilterCount(filter) > 0 || !!q.trim();

  if (groups.active.length + groups.disabled.length + groups.paused.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, position: 'relative' }}>
        {hasFilter ? (
          <Box component="button" onClick={() => onFilter({ facets: {} })}
            sx={{ fontSize: 13, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }}>
            {t('promptStudio.conv.clearAll')}
          </Box>
        ) : (
          <Button variant="contained" size="small" onClick={() => onCreating(true)} sx={{ fontWeight: 600 }}>
            {t('promptStudio.conv.new')}
          </Button>
        )}
        {filtersOpen && (
          <ConversationFiltersPanel personas={data.personas} q={q} filter={filter} onChange={onFilter}
            values={values} searchText={searchText} labelOf={labelOf} onClose={onCloseFilters} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ColumnHeader />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {groups.active.map((p) => (
          <ConversationRow
            key={p.personaId}
            persona={p}
            title={personaTitle(p, names)}
            intentLabel={intentLabel(p.intentId)}
            open={openId === p.personaId}
            onToggle={() => setOpenId(openId === p.personaId ? null : p.personaId)}
          >
            {formFor(p, () => setOpenId(null))}
          </ConversationRow>
        ))}
        {groups.disabled.length > 0 && (
          <ConvGroupHeader label={t('promptStudio.disabled.group')} count={groups.disabled.length} note={t('promptStudio.disabled.noteConv')} />
        )}
        {groups.disabled.map((p) => (
          <ConversationPausedRow key={p.personaId} persona={p} title={personaTitle(p, names)} variant="user"
            brandId={brand.brandId} isAgency={isAgency} onReactivated={onSaved} onReload={onReload} />
        ))}
        {groups.paused.length > 0 && (
          <ConvGroupHeader label={t('promptStudio.paused.group')} count={groups.paused.length} note={t('promptStudio.paused.note')} />
        )}
        {groups.paused.map((p) => (
          <ConversationPausedRow key={p.personaId} persona={p} title={personaTitle(p, names)} variant="plan"
            brandId={brand.brandId} isAgency={isAgency} onReactivated={onSaved} onReload={onReload} />
        ))}
      </Box>
      {filtersOpen && (
        <ConversationFiltersPanel personas={data.personas} q={q} filter={filter} onChange={onFilter}
          values={values} searchText={searchText} labelOf={labelOf} onClose={onCloseFilters} />
      )}
    </Box>
  );
}

/** « Désactivés » / « Mis en pause (offre) » header: hollow grey ring, label, count, then its note (§2.3). */
function ConvGroupHeader({ label, count, note }: { label: string; count: number; note: string }) {
  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 2, px: 2, py: 0.75, bgcolor: '#f4f4f6e6', borderBottom: '1px solid', borderColor: 'rgba(0,0,0,.07)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12.5, fontWeight: 700, color: 'text.secondary' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: '1.5px solid #9ca3af', flexShrink: 0 }} />
        {label}
        <Box sx={{ fontWeight: 400, opacity: 0.7 }}>{count}</Box>
      </Box>
      <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.25 }}>{note}</Box>
    </Box>
  );
}

function ColumnHeader() {
  const { t } = useTranslation();
  const cell = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.04em', color: 'text.secondary' };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fafbfd', borderBottom: '1px solid', borderColor: 'divider', py: 0.75, flexShrink: 0 }}>
      <Box sx={{ width: 6, flexShrink: 0 }} />
      <Box sx={{ ...cell, flex: 1, px: 1.5 }}>{t('promptStudio.conv.col.conversation')}</Box>
      <Box sx={{ ...cell, width: 168, minWidth: 168, px: 0.5 }}>{t('promptStudio.conv.col.place')}</Box>
      <Box sx={{ ...cell, width: 68, minWidth: 68, px: 0.5 }}>{t('promptStudio.conv.col.languages')}</Box>
      <Box sx={{ ...cell, width: 60, minWidth: 60, textAlign: 'right', pr: 0.5 }}>{t('promptStudio.conv.col.runs')}</Box>
      <Box sx={{ width: 18, flexShrink: 0 }} />
    </Box>
  );
}
